import type { ApiError } from '@/api/generated/types'
import { accessToken, clearTokens, hasSession, needsRenewal, refreshTokens, type RefreshOutcome } from '@/api/intchAuth'
import { operatorToken } from '@/utils/operatorToken'

/**
 * The service answers `{ error, code }` with a non-2xx and JSON on success, so a hand-written
 * fetch wrapper is the whole client — there is no OpenAPI schema to generate from. Same-origin
 * `/api/*` in every environment: the dev server proxies it, production serves both from one host.
 *
 * The comment here used to say there was no auth to attach because the surface sat behind basic auth
 * at the edge. That stopped being true on 2026-08-26, when the edge password was removed so the
 * payment gateway could return buyers to `/thank-you`. What travels now is the OPERATOR token, when
 * the machine has one, and the buyer's own Intch token (`Authorization: Bearer`) once they have signed
 * in — see api/intchAuth.ts. The service asks Intch whose token it is; nothing here claims an identity.
 */
export class RequestError extends Error {
  /* Fields written out longhand, not as parameter properties: node's type stripping cannot run
     those, and this file is reached from the node-run client tests (searchHistory → accountPush). */
  readonly status: number
  readonly code: string | null
  /**
   * THE SERVICE'S OWN SENTENCE, or null when this message is one WE made up here.
   *
   * The two are not interchangeable and telling them apart is the whole point of this field.
   * `POST /api/search returned 403 with a non-JSON body` is a line this file writes when the edge
   * answers instead of the service; the stamp-mismatch paragraph is a line the ENGINE writes for a
   * person to read. A screen that prints the first one under "here is the rule that stopped it"
   * hands the buyer an endpoint path as if it were a product rule — which is what happened while
   * every 4xx was treated alike (measured 2026-09-22). Only what the service actually said may be
   * shown; see launchFailure.ts.
   */
  readonly said: string | null

  constructor(message: string, status: number, code: string | null = null, said: string | null = null) {
    super(message)
    this.name = 'RequestError'
    this.status = status
    this.code = code
    this.said = said
  }
}

export type RequestOptions = {
  signal?: AbortSignal
  // Model calls run 30-120s server-side; a default fetch has no timeout at all, and a hung
  // request that never rejects leaves an AsyncData spinning forever. What each KIND of call is
  // allowed to take, and why, is the table in api/timeouts.ts — pass one of those.
  timeoutMs?: number
}

/**
 * THE SAFETY NET, NOT A BUDGET. It catches the call nobody assigned a budget to, so 900_000 is the
 * LOOSEST number in api/timeouts.ts on purpose: a forgotten budget must never be stricter than a
 * deliberate one.
 *
 * It was 180_000, and on 2026-09-22 a buyer's search died on it. The service does not notice a
 * client hanging up (http/router.ts has no `req.on('close')`), so the search ran to the end, the run
 * was created and paid for out of quota — and the only copy of its id was in the answer the browser
 * had already thrown away. The screen printed the browser's own words for it, "signal timed out".
 */
const DEFAULT_TIMEOUT_MS = 900_000

/**
 * Вход закончился — монолит отверг refresh (не «не ответил»: тогда вход продолжается). Слушает стор входа (stores/authStore.ts): сам транспорт про
 * экраны не знает, а стор уводит человека на вход.
 */
const lostListeners = new Set<() => void>()
export const onSignedOut = (listener: () => void) => {
  lostListeners.add(listener)
  return () => lostListeners.delete(listener)
}

/**
 * ОДИН refresh на весь клиент одновременно. Access живёт 20 минут, и когда он истекает, на 401 падают
 * сразу все запросы экрана; без замка каждый из них обновил бы пару сам, а ротация делает все
 * refresh, кроме первого, недействительными — человек выходил бы из аккаунта на ровном месте.
 */
let refreshing: Promise<RefreshOutcome> | null = null
const refreshOnce = () => {
  refreshing ??= refreshTokens().finally(() => {
    refreshing = null
  })
  return refreshing
}

/** Обновить пару; true — есть новый access. Отвергнутый refresh при живом входе — выход. */
export const renew = async (): Promise<boolean> => {
  const outcome = await refreshOnce()
  if (outcome === 'rejected' && hasSession()) {
    clearTokens()
    for (const listener of lostListeners) listener()
  }
  return outcome === 'ok'
}

const request = async <T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  retried = false,
): Promise<T> => {
  const timeout = AbortSignal.timeout(options.timeoutMs ?? DEFAULT_TIMEOUT_MS)
  const signal = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout

  /* Read per REQUEST, never snapshotted at import: the console stores the token after the module has
     loaded, and a snapshot would leave every call in that first session unauthenticated. */
  const token = operatorToken()
  /* Заранее, а не по 401: см. needsRenewal. Не вышло из-за сбоя — запрос идёт со старым токеном. */
  if (!retried && needsRenewal()) await renew()
  /* Тоже на каждый запрос: cookie перезаписывает refresh, в том числе с другой вкладки. */
  const bearer = accessToken()

  const response = await fetch(path, {
    method,
    signal,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { 'X-Operator-Token': token } : {}),
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  const text = await response.text()
  let parsed: unknown = null
  try {
    parsed = text ? JSON.parse(text) : null
  } catch {
    // A non-JSON body means the proxy or the edge answered, not the service.
    throw new RequestError(
      `${method} ${path} returned ${response.status} with a non-JSON body`,
      response.status,
    )
  }

  if (!response.ok) {
    const { error, code } = (parsed ?? {}) as ApiError
    /* AUTH_REQUIRED — единственный код, на который обновляется токен: NOT_YOUR_RUN и AUTH_UPSTREAM
       новый токен не исправит, а выход из аккаунта из-за сбоя монолита был бы худшим ответом. */
    if (response.status === 401 && code === 'AUTH_REQUIRED' && !retried && bearer) {
      if (await renew()) return request<T>(method, path, body, options, true)
    }
    // `said` is the body's own `error` and NOTHING else: a 4xx whose body carries no sentence is a
    // refusal we cannot quote, and the fallback below is our words, not the service's.
    throw new RequestError(error || `${method} ${path} failed`, response.status, code ?? null, error || null)
  }

  return parsed as T
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
}

/**
 * Query strings are built here rather than in the generated client: `undefined` and `false` must not
 * become the strings "undefined" and "false", and that rule belongs with the transport.
 */
export const withQuery = (path: string, query: Record<string, unknown>): string => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === false) continue
    params.set(key, value === true ? '1' : String(value))
  }
  const search = params.toString()
  return search ? `${path}?${search}` : path
}
